import React, { useEffect, useMemo, useRef } from 'react';
import { format, getDay, parseISO } from 'date-fns';
import { el } from 'date-fns/locale';
import {
  Calendar as CalendarIcon,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { TNaerasCalendar } from '@/data/types';

interface OfferCalendarProps {
  selectedDates: string[]; // YYYY-MM-DD format
  onDatesChange: (dates: string[]) => void;
  /** The selectable window, as the backend defines it (Europe/Athens). */
  calendar: TNaerasCalendar | null;
}

/**
 * One column per selectable day on wide screens.
 *
 * The window is whatever the backend's `horizon_days` says — 7 today — so a
 * hardcoded number left empty columns and the cards stretched off to the left.
 * Spelled out rather than interpolated so Tailwind can still see the classes.
 */
const WIDE_GRID_COLUMNS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
  7: 'lg:grid-cols-7',
};

/** Every date from `from` to `to`, inclusive, as YYYY-MM-DD. */
function dateRange(from: string, to: string): string[] {
  const dates: string[] = [];
  const cursor = parseISO(from);
  const end = parseISO(to);

  while (cursor <= end) {
    dates.push(format(cursor, 'yyyy-MM-dd'));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

export const OfferCalendar: React.FC<OfferCalendarProps> = ({
  selectedDates,
  onDatesChange,
  calendar,
}) => {
  const didSeedSelection = useRef(false);

  const windowDates = useMemo(() => {
    if (!calendar) return [];
    let dates = dateRange(calendar.min_date, calendar.max_date);
    const extraDates = selectedDates.filter(d => !dates.includes(d));
    if (extraDates.length > 0) {
      dates = [...extraDates.sort(), ...dates];
    }
    return dates;
  }, [calendar, selectedDates]);

  const holidaysByDate = useMemo(() => {
    const map: Record<string, string> = {};
    calendar?.holidays?.forEach((h) => {
      map[h.date] = h.name;
    });

    return map;
  }, [calendar]);

  // The backend suggests the pre-selection: normally tomorrow, on Fridays the
  // whole Sat/Sun/Mon block, because ΔΕΔΔΗΕ doesn't take bids at the weekend.
  useEffect(() => {
    if (!calendar || didSeedSelection.current) return;

    didSeedSelection.current = true;

    if (selectedDates.length === 0) {
      onDatesChange(calendar.suggested_dates);
    }
  }, [calendar, selectedDates.length, onDatesChange]);

  if (!calendar) {
    return (
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-blue-950/5">
        <div className="h-24 animate-pulse rounded-2xl bg-gray-100" />
      </div>
    );
  }

  const minDate = calendar.min_date;
  const isMultiDaySuggestion = calendar.suggested_dates.length > 1;

  /**
   * Keeps the selection one unbroken run of days.
   *
   * Each later day's pool check subtracts every day before it, so a gap would
   * leave a day out of that sum. Picking a day outside the run stretches the run
   * to reach it; dropping the first day shortens it from the front, and
   * dropping any other day ends the run just before it. The run doesn't have to
   * start at tomorrow — a single mid-window day is still a normal pick.
   */
  const handleDateClick = (dateStr: string) => {
    const index = windowDates.indexOf(dateStr);
    const selectedIndexes = windowDates
      .map((d, i) => (selectedDates.includes(d) ? i : -1))
      .filter((i) => i >= 0);

    if (selectedIndexes.length === 0) {
      onDatesChange([dateStr]);
      return;
    }

    const first = selectedIndexes[0];
    const last = selectedIndexes[selectedIndexes.length - 1];

    const [from, to] = !selectedDates.includes(dateStr)
      ? [Math.min(first, index), Math.max(last, index)]
      : index === first
      ? [first + 1, last]
      : [first, index - 1];

    onDatesChange(from > to ? [] : windowDates.slice(from, to + 1));
  };

  const sortedSelectedDates = [...selectedDates].sort();
  const showAccuracyWarning = selectedDates.length > calendar.warn_above_days;

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-xl shadow-blue-950/5 backdrop-blur-sm transition-all">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/20">
            <CalendarIcon className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Επιλογή Ημερομηνιών Προσφοράς</h2>
            <p className="text-xs text-gray-500">
              Επιτρεπτός ορίζοντας: {format(parseISO(minDate), 'd MMM', { locale: el })} -{' '}
              {format(parseISO(calendar.max_date), 'd MMM yyyy', { locale: el })} (συνεχόμενες
              ημέρες, μέγιστο {windowDates.length})
            </p>
          </div>
        </div>

        {/* Friday badge */}
        {isMultiDaySuggestion && (
          <div className="flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50/80 px-3 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
            <span>Αυτόματη επιλογή 3ημέρου (Σαβ, Κυρ, Δευ) - Ξεκλείδωτη</span>
          </div>
        )}
      </div>

      {/* Multi-day accuracy warning */}
      <AnimatePresence>
        {showAccuracyWarning && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 overflow-hidden"
          >
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/90 p-4 text-amber-900 shadow-sm">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
              <div>
                <p className="text-sm font-semibold">
                  Προσοχή: Η ακρίβεια των προβλέψεων μειώνεται σε τόσο μεγάλο χρονικό ορίζοντα.
                </p>
                <p className="mt-0.5 text-xs text-amber-800">
                  Έχετε επιλέξει {selectedDates.length} ημέρες. Οι υπολογισμοί προσφοράς θα εμφανιστούν σε καρτέλες για κάθε μέρα.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Date Cards Selector Grid */}
      <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4 ${WIDE_GRID_COLUMNS[windowDates.length] ?? 'lg:grid-cols-4'}`}>
        {windowDates.map((dateStr) => {
          const dateObj = parseISO(dateStr);
          const isSelected = selectedDates.includes(dateStr);
          const selectedOrderIndex = isSelected ? sortedSelectedDates.indexOf(dateStr) : -1;
          // Day 4th and beyond get the red highlight.
          const isExtendedWarningDay = isSelected && selectedOrderIndex >= 3;

          const holidayName = holidaysByDate[dateStr];
          const dayOfWeek = getDay(dateObj);
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6

          const dayName = format(dateObj, 'EEEE', { locale: el });
          const formattedDate = format(dateObj, 'dd/MM/yyyy');

          let cardStyle = 'border-gray-200 bg-gray-50/50 hover:border-gray-300 hover:bg-white';
          if (isSelected) {
            if (isExtendedWarningDay) {
              cardStyle =
                'border-rose-500 bg-gradient-to-br from-rose-50 to-red-50/60 shadow-md shadow-rose-500/15 ring-1 ring-rose-400/40';
            } else {
              cardStyle =
                'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50/50 shadow-md shadow-blue-500/10';
            }
          }

          return (
            <motion.div
              key={dateStr}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleDateClick(dateStr)}
              className={`relative cursor-pointer rounded-2xl border p-3.5 transition-all duration-200 flex flex-col justify-between ${cardStyle}`}
            >
              {/* Top Row: Day Name & Checkbox */}
              <div className="flex items-start justify-between gap-1">
                <span className="capitalize text-xs font-bold text-gray-500 truncate">{dayName}</span>
                <div
                  className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-md border transition-colors ${
                    isSelected
                      ? isExtendedWarningDay
                        ? 'border-rose-600 bg-rose-600 text-white'
                        : 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {isSelected && <CheckCircle2 className="h-3.5 w-3.5" />}
                </div>
              </div>

              {/* Date Value */}
              <div className="my-2">
                <div className="text-base font-bold text-gray-900">{formattedDate}</div>
                {isExtendedWarningDay && (
                  <span className="inline-block mt-0.5 rounded-md bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">
                    Ημέρα {selectedOrderIndex + 1}η
                  </span>
                )}
              </div>

              {/* Holiday or Weekend or Working Day Badge */}
              <div className="mt-auto pt-1">
                {holidayName ? (
                  <div className="inline-flex items-center gap-1 rounded-lg border border-purple-200 bg-purple-100/90 px-2 py-0.5 text-[10px] font-semibold text-purple-900">
                    <span className="h-1.5 w-1.5 rounded-full bg-purple-600" />
                    <span className="truncate" title={holidayName}>Αργία: {holidayName}</span>
                  </div>
                ) : isWeekend ? (
                  <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-100/80 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                    <span>Σαββατοκύριακο</span>
                  </div>
                ) : (
                  <div className="text-[11px] text-gray-500">Εργάσιμη ημέρα</div>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Legend / Today Context Bar */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs text-gray-500">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-600" />
          <span>
            Σημερινή Ημερομηνία: <strong>{format(parseISO(calendar.today), 'dd/MM/yyyy')}</strong>
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-blue-500" /> Επιλεγμένη (1-3 μέρες)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-rose-500" /> Επιλεγμένη (4η μέρα και μετά)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-purple-600" /> Αργία
          </span>
        </div>
      </div>
    </div>
  );
};

export default OfferCalendar;
